/**
 * Historial de pedidos (ADM-09).
 *
 * La columna que importa no es el total: es si el pedido quedo registrado en
 * Random. Un pedido pagado que no llego a Random no aparece en la contabilidad
 * de la ferreteria, y por eso se marca en rojo y se puede aislar con un filtro.
 *
 * **Filtros y detalle (22-09-2026).** Buscador, pago, Random, entrega,
 * documento, fechas, total y orden, combinables y en la URL
 * (`utilidades/filtros-pedidos.ts`), como en Productos. Cada fila enlaza a
 * `?pedido=<id>`, que abre `ModalPedido.tsx` con todo lo que la tabla no
 * muestra; el enlace se estira a la fila entera (`.fila-enlace`) porque la
 * fila no tiene otro control. Las columnas se ajustan a su texto: solo el
 * cliente puede bajar de linea (`.sin-cortes` en las demas). Abrir y cerrar reemplazan la entrada del
 * historial: el modal no es una pagina a la que se vuelva con "Atras".
 */
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FiltrosActivos } from '../../componentes/administracion/FiltrosActivos';
import { FiltrosPedidos } from '../../componentes/administracion/FiltrosPedidos';
import { ModalPedido } from '../../componentes/administracion/ModalPedido';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { Paginador } from '../../componentes/catalogo/Paginador';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerPedidos } from '../../servicios/administracion.servicio';
import {
  conCambio,
  escribirFiltros,
  filtrosActivos,
  leerFiltros,
  PARAMETRO_PEDIDO,
  sinFiltros,
} from '../../utilidades/filtros-pedidos';
import type { FiltrosPedidos as Filtros } from '../../utilidades/filtros-pedidos';
import { fechaHora, numero, pesos } from '../../utilidades/formato';
import { conPagina, rutas } from '../../utilidades/rutas';
import { documento, ESTADO_PAGO, rotuloRandom } from '../../utilidades/textos-pedido';

function idDe(valor: string | null): number | null {
  const id = Number(valor);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function Pedidos() {
  const [parametros, setParametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const filtros = leerFiltros(parametros);
  // La cadena es la clave estable de los filtros: el objeto se rehace en cada render.
  const consultaFiltros = escribirFiltros(filtros).toString();
  const termino = filtros.termino ?? '';
  const pedidoAbierto = idDe(parametros.get(PARAMETRO_PEDIDO));

  const { estado, datos, error } = usePeticion(
    (senal) => obtenerPedidos({ ...filtros, pagina }, senal),
    [pagina, consultaFiltros],
  );

  const activos = filtrosActivos(filtros);

  // Cambiar un filtro vuelve a la pagina 1 y cierra el modal.
  function aplicarFiltros(siguientes: Filtros): void {
    setParametros(escribirFiltros(siguientes));
  }

  function buscar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const valor = new FormData(evento.currentTarget).get('termino');
    const texto = typeof valor === 'string' ? valor.trim() : '';
    aplicarFiltros(conCambio(filtros, { termino: texto.length > 0 ? texto : undefined }));
  }

  const rutaListado =
    consultaFiltros.length > 0
      ? `${rutas.administracion.pedidos()}?${consultaFiltros}`
      : rutas.administracion.pedidos();
  const rutaActual = conPagina(rutaListado, pagina);

  /** La URL de hoy con el modal de `id` abierto (pagina y filtros intactos). */
  function rutaConPedido(id: number): string {
    const siguientes = new URLSearchParams(parametros);
    siguientes.set(PARAMETRO_PEDIDO, String(id));
    return `?${siguientes.toString()}`;
  }

  function cerrarPedido(): void {
    const siguientes = new URLSearchParams(parametros);
    siguientes.delete(PARAMETRO_PEDIDO);
    setParametros(siguientes, { replace: true });
  }

  return (
    <>
      <Seo
        titulo="Pedidos"
        descripcion="Historial de pedidos y su registro en Random."
        ruta={rutas.administracion.pedidos()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Pedidos"
        bajada={
          datos === null
            ? undefined
            : activos.length > 0
              ? `${numero(datos.total)} cumplen los filtros`
              : `${numero(datos.total)} en el historial`
        }
        acciones={
          <form onSubmit={buscar} role="search">
            <label className="solo-lectores" htmlFor="buscar-pedidos">
              Buscar pedidos por referencia, cliente, correo, RUT, folio o código de producto
            </label>
            {/* `key`: si un chip quita la busqueda, el campo se vacia. */}
            <input
              key={termino}
              id="buscar-pedidos"
              name="termino"
              type="search"
              className="entrada"
              placeholder="Referencia, cliente, RUT, folio..."
              defaultValue={termino}
            />
            <button type="submit" className="btn btn-sm btn-primario">
              <Icono nombre="buscar" />
              Buscar
            </button>
          </form>
        }
      />

      <div className="admin-cuerpo">
        <FiltrosPedidos filtros={filtros} onCambiar={aplicarFiltros} />
        <FiltrosActivos
          activos={activos}
          onCambiar={aplicarFiltros}
          onLimpiar={() => {
            aplicarFiltros(sinFiltros(filtros));
          }}
        />

        {estado === 'cargando' ? <Cargando que="los pedidos" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null && datos.items.length === 0 ? (
          <div className="panel-vacio" role="status">
            <p>
              {activos.length > 0
                ? 'Ningún pedido cumple estos filtros.'
                : 'Todavía no hay pedidos.'}
            </p>
            {activos.length > 0 ? (
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => {
                  aplicarFiltros(sinFiltros(filtros));
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
          </div>
        ) : null}

        {datos !== null && datos.items.length > 0 ? (
          <>
            <Tabla
              titulo="Historial de pedidos"
              encabezados={
                <>
                  <th scope="col">Pedido</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Entrega</th>
                  <th scope="col">Documento</th>
                  <th scope="col" className="der">
                    Total
                  </th>
                  <th scope="col">Pago</th>
                  <th scope="col">Random</th>
                </>
              }
            >
              {datos.items.map((pedido) => {
                const pago = ESTADO_PAGO[pedido.estado];
                const random = rotuloRandom(pedido);
                const unidades = pedido.items.reduce((suma, item) => suma + item.cantidad, 0);
                return (
                  <tr
                    key={pedido.id}
                    className={pedido.id === pedidoAbierto ? 'fila-enlace fila-seleccionada' : 'fila-enlace'}
                  >
                    <th scope="row" className="sin-cortes">
                      <Link className="enlace-fila" to={rutaConPedido(pedido.id)} replace>
                        <span className="mono">{pedido.referencia}</span>
                        <span className="solo-lectores">, ver el detalle</span>
                      </Link>
                      <span className="s">
                        <time dateTime={pedido.creadoEn}>{fechaHora(pedido.creadoEn)}</time>
                      </span>
                    </th>
                    <td className="partible">
                      {pedido.contacto.nombre}
                      <span className="s">
                        {numero(unidades)} {unidades === 1 ? 'producto' : 'productos'}
                      </span>
                    </td>
                    <td className="sin-cortes">
                      {pedido.tipoEntrega === 'retiro' ? 'Retiro en tienda' : 'Despacho'}
                      {pedido.direccion !== null ? (
                        <span className="s">{pedido.direccion.comuna}</span>
                      ) : null}
                    </td>
                    <td className="sin-cortes">{documento(pedido)}</td>
                    <td className="der num">{pesos(pedido.total)}</td>
                    <td className="sin-cortes">
                      <span className={pago.clase}>{pago.texto}</span>
                    </td>
                    <td className="sin-cortes">
                      <span className={random.clase}>{random.texto}</span>
                    </td>
                  </tr>
                );
              })}
            </Tabla>

            <Paginador
              pagina={datos.pagina}
              total={datos.total}
              porPagina={datos.porPagina}
              ruta={rutaListado}
            />
          </>
        ) : null}
      </div>

      {/* Volver desde la ficha de un producto reabre este mismo pedido. */}
      <ModalPedido
        id={pedidoAbierto}
        volver={
          pedidoAbierto !== null
            ? `${rutaActual}${rutaActual.includes('?') ? '&' : '?'}${PARAMETRO_PEDIDO}=${pedidoAbierto}`
            : rutaActual
        }
        onCerrar={cerrarPedido}
      />
    </>
  );
}
